import React, { Suspense } from "react";
import { Navigate, useParams, useRoutes } from "react-router-dom";

// Layouts
const DashboardLayout = React.lazy(() => import('./layouts/dashboard'));
const SimpleLayout = React.lazy(() => import('./layouts/simple'));

// Pages
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPassword'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const Page404 = React.lazy(() => import('./pages/Page404'));
const DashboardAppPage = React.lazy(() => import('./pages/DashboardAppPage'));
const Candidates = React.lazy(() => import('./pages/Candidate'));
const JobStatus = React.lazy(() => import('./pages/JobStatus'));
const ResumeBuilder = React.lazy(() => import('./pages/ResumeBuilder'));
const Jobposts = React.lazy(() => import('./pages/Jobpost'));
const Users = React.lazy(() => import('./pages/Users'));
const Masters = React.lazy(() => import('./pages/Master'));
const Profile = React.lazy(() => import('./pages/Profile'));

const UserProfile = React.lazy(() => import('./pages/userProfile'));
const Client = React.lazy(() => import('./pages/Client'));
const EmpChatMain = React.lazy(() => import('./pages/EmpChat'))
const EmailTransaction = React.lazy(() => import('./pages/EmailTransaction'))
const EmailTemplates = React.lazy(() => import('./pages/EmailTemplates'))
const ResumeMasters = React.lazy(() => import('./pages/Master/Resume'))
const UserProject = React.lazy(() => import('./pages/userProject'));
const AgentDashboard = React.lazy(() => import('src/ChatComponent/AgentDashboard'));

const Project = React.lazy(() => import('./pages/Project'));

const Env = React.lazy(() => import('./pages/env/env'));
const CandidateLoginPage = React.lazy(() => import('./pages/CandidateLoginpage'));
const ProjectConsole = React.lazy(() => import('./pages/console/Operations'));
const CandidateRegisterForm = React.lazy(() => import('./sections/auth/login/CandidateRegisterForm'));
const CandidateInterviewPage = React.lazy(() => import('./pages/Interview'))
const MailJobPost = React.lazy(() => import('./components/Krishpar/MailJobView/MailJobView'));
const PrivacyPolicy = React.lazy(() => import('./components/Privacy-policy/Privacy-policy'));
const TermsOfService = React.lazy(() => import('./components/Terms-of-service/Terms-of-service'));

function PrivateSalesRoute({ isAuthenticated, userDesignation, children }) {
  const { designation } = useParams();

  if (isAuthenticated !== "Sales") {
    return <Navigate to="/login" />;
  }

  if (designation !== userDesignation) {
    return <Navigate to="/404" />;
  }

  return children;
}

function PrivateOperationsRoute({ isAuthenticated, userDesignation, children }) {
  const { designation } = useParams();

  if (isAuthenticated !== "Operations") {
    return <Navigate to="/login" />;
  }

  if (designation !== userDesignation) {
    return <Navigate to="/404" />;
  }

  return children;
}

export default function Router({ isAuthenticated, onLogin, onLogout, userDesignation }) {
  const routes = useRoutes([
    // Super-Admin
    {
      path: "/admin",
      element: isAuthenticated === "Super-Admin" ? (
        <DashboardLayout onLogout={onLogout} />
      ) : (
        <Navigate to="/login" />
      ),
      children: [
        { path: "dashboard", element: <DashboardAppPage /> },
        { path: "candidates", element: <Candidates /> },
        { path: "candidates-new", element: <Candidates /> },
        { path: "jobposts", element: <Jobposts /> },
        { path: "users", element: <Users /> },
        { path: "operations-console", element: <ProjectConsole /> },
        { path: "email-transactions", element: <EmailTransaction /> },
        // { path: "email-templates", element: <EmailTemplates /> },
        { path: "master", element: <Masters /> },
        { path: "keywords", element: <ResumeMasters /> },
        { path: "profile", element: <Profile /> },
        { path: "clients", element: <Client /> },
        { path: "emp-chat", element: <EmpChatMain /> },
        { path: "project", element: <Project /> },
        { path: "server", element: <Env /> },
        { path: "resume-builder/:candidateId", element: <ResumeBuilder /> },
        { path: "privacy-policy", element: <PrivacyPolicy /> },
        { path: "terms-of-service", element: <TermsOfService /> },
      ],
    },
    // Sales
    {
      path: "/sales/:designation",
      element: (
        <PrivateSalesRoute isAuthenticated={isAuthenticated} userDesignation={userDesignation}>
          <DashboardLayout onLogout={onLogout} />
        </PrivateSalesRoute>
      ),
      children: [
        { path: "dashboard", element: <DashboardAppPage /> },
        { path: "profile", element: <Profile /> },
        { path: "candidates", element: <Candidates /> },
        { path: "jobposts", element: <Jobposts /> },
        { path: "clients", element: <Client /> },
        { path: "emp-chat", element: <EmpChatMain /> },
        { path: "userProfile", element: <UserProfile /> },
        { path: "operations-console", element: <ProjectConsole /> },
        { path: "users", element: <Users /> },
        { path: "resume-builder/:candidateId", element: <ResumeBuilder /> },
        { path: "privacy-policy", element: <PrivacyPolicy /> },
        { path: "terms-of-service", element: <TermsOfService /> },
      ],
    },
    // Operations
    {
      path: "/operations/:designation",
      element: (
        <PrivateOperationsRoute isAuthenticated={isAuthenticated} userDesignation={userDesignation}>
          <DashboardLayout onLogout={onLogout} />
        </PrivateOperationsRoute>
      ),
      children: [
        { path: "dashboard", element: <DashboardAppPage /> },
        { path: "profile", element: <Profile /> },
        { path: "candidates", element: <Candidates /> },
        { path: "jobposts", element: <Jobposts /> },
        { path: "clients", element: <Client /> },
        { path: "emp-chat", element: <EmpChatMain /> },
        { path: "userProfile", element: <UserProfile /> },
        { path: "operations-console", element: <ProjectConsole /> },
        { path: "users", element: <Users /> },
        { path: "resume-builder/:candidateId", element: <ResumeBuilder /> },
        { path: "privacy-policy", element: <PrivacyPolicy /> },
        { path: "terms-of-service", element: <TermsOfService /> },
      ],
    },
    // Candidate
    {
      path: "/candidate",
      element: isAuthenticated === "Candidate" ? (
        <DashboardLayout onLogout={onLogout} />
      ) : (
        <CandidateLoginPage onLogin={onLogin} />
      ),
      children: [
        {
          path: '',
          element: <Navigate to={
            localStorage.getItem('JobPostId') ? 'jobposts' : 'profile'
          } />
        }, // default redirect
        { path: 'profile', element: <Profile /> },
        { path: 'job/status', element: <JobStatus /> },
        { path: 'interview', element: <CandidateInterviewPage isCandidate={true} /> },
        { path: 'resume-builder/:candidateId', element: <ResumeBuilder /> },
        { path: "jobposts", element: <Jobposts isCandidate={true} /> },
        { path: "privacy-policy", element: <PrivacyPolicy /> },
        { path: "terms-of-service", element: <TermsOfService /> },
      ],
    },
    {
      path: '/register/candidate',
      element: <CandidateRegisterForm />
    },
    {
      path: '/mail/candidate/job/post',
      element: <MailJobPost />
    },
    {
      path: "/privacy-policy",
      element: <PrivacyPolicy />
    },
    {
      path: "/terms-of-service",
      element: <TermsOfService />
    },
    // Public
    {
      path: "/login",
      element:
        isAuthenticated === "Super-Admin" ? (
          <Navigate to="/admin/dashboard" />
        ) : isAuthenticated === "Sales" ? (
          <Navigate to={`/sales/${userDesignation}/userProfile`} />
        ) : isAuthenticated === "Operations" ? (
          <Navigate to={`/operations/${userDesignation}/userProfile`} />
        ) : isAuthenticated === "HR" ? (
          <Navigate to="/hr/dashboard" />
        ) : (
          <LoginPage onLogin={onLogin} />
        ),
    },
    {
      path: "/password/reset",
      element: <ResetPasswordPage />,
    },
    {
      element: <SimpleLayout />,
      children: [
        { index: true, element: <Navigate to="/login" /> },
        { path: "404", element: <Page404 /> },
        { path: "*", element: <Navigate to="/404" /> },
      ],
    },
  ]);

  return routes;
}